import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { identity } from '@repletoryreach/sdk-api';
import { useT } from '../../../../i18n/I18nProvider';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** The keys the operator selected. Ids only; the count and the org spread are shown. */
  ids: string[];
  orgCount: number;
  onDone: () => void;
}

/**
 * Revokes a whole selection in one call.
 *
 * This used to be one `DELETE` per key in a loop, which could stop halfway and
 * leave a selection an operator believes is off half live. `POST
 * /v1/admin/api-keys/revoke` is one transaction, so the answer is either "all
 * of them" or "none of them", and it reports keys that were already revoked
 * separately rather than counting them as work done.
 *
 * The reason is required by the endpoint, and required here for the same
 * reason: a revocation nobody can explain three months later is the one that
 * gets quietly undone. It is stored on every row and written to the security
 * feed with the operator's name.
 */
export function RevokeSelectionDrawer({ opened, onClose, ids, orgCount, onDone }: Props) {
  const t = useT();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');

  useEffect(() => { if (opened) setReason(''); }, [opened]);

  const revoke = useMutation({
    mutationFn: () => identity.adminRevokeApiKeys(ids, reason.trim()),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'api-keys'] });
      const d = res.data;
      // "12 revoked, 1 was already off" is the honest sentence. Rolling the
      // second number into the first would tell an operator they turned off a
      // key that somebody else had already dealt with.
      notifySuccess(
        d.already_revoked > 0
          ? t('settings.apiKeys.selection.revokedWithSkipped', { count: d.revoked, skipped: d.already_revoked })
          : t('settings.apiKeys.selection.revokedToast', { count: d.revoked }),
        t('settings.apiKeys.toast.revokedTitle')
      );
      onDone();
      onClose();
    },
    onError: (err: Error) => {
      notifyError(err.message, t('settings.apiKeys.toast.revokeFailedTitle'));
    }
  });

  const tooMany = ids.length > 100;
  const canSubmit = ids.length > 0 && !tooMany && reason.trim().length > 0;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t('settings.apiKeys.selection.revokeTitle', { count: ids.length })}
      position="right"
    >
      <Stack gap="md">
        <Text role="bodySmall">
          {t('settings.apiKeys.selection.revokeBody', { count: ids.length, orgs: orgCount })}
        </Text>

        {tooMany && (
          <Alert color="warning" title={t('settings.apiKeys.selection.tooManyTitle')}>
            {t('settings.apiKeys.selection.tooManyBody')}
          </Alert>
        )}

        <Textarea
          label={t('settings.apiKeys.selection.reasonLabel')}
          description={t('settings.apiKeys.selection.reasonHint')}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={3}
          required
        />

        <Group gap="sm">
          <Button
            size="xs"
            color="red"
            disabled={!canSubmit}
            loading={revoke.isPending}
            onClick={() => revoke.mutate()}
          >
            {t('settings.apiKeys.selection.revoke', { count: ids.length })}
          </Button>
          <Button size="xs" variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
